import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const POPOVER_WIDTH = 312;

function formatMonthKey(year, monthIndex) {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

function formatMonthDisplay(monthKey) {
  if (!monthKey) return '';
  const [year, month] = monthKey.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString('en-PK', {
    month: 'long',
    year: 'numeric',
  });
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function ShopMonthPicker({
  value,
  onChange,
  monthsData = [],
  placeholder = 'Select month',
}) {
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [hoverMonth, setHoverMonth] = useState('');
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const monthMap = useMemo(() => {
    const map = new Map();
    monthsData.forEach((row) => map.set(row.month, row));
    return map;
  }, [monthsData]);

  const years = useMemo(() => {
    const set = new Set(monthsData.map((row) => Number(row.month.split('-')[0])));
    if (!set.size) set.add(new Date().getFullYear());
    return Array.from(set).sort((a, b) => a - b);
  }, [monthsData]);

  const initialYear = value
    ? Number(value.split('-')[0])
    : years[years.length - 1] || new Date().getFullYear();

  const [viewYear, setViewYear] = useState(initialYear);

  useEffect(() => {
    if (value) {
      setViewYear(Number(value.split('-')[0]));
    }
  }, [value]);

  const updatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const popoverHeight = popoverRef.current?.offsetHeight || 360;
    const viewportPadding = 12;

    let left = rect.left;
    let top = rect.bottom + 8;

    if (left + POPOVER_WIDTH > window.innerWidth - viewportPadding) {
      left = window.innerWidth - POPOVER_WIDTH - viewportPadding;
    }
    if (left < viewportPadding) left = viewportPadding;

    if (top + popoverHeight > window.innerHeight - viewportPadding) {
      top = rect.top - popoverHeight - 8;
    }
    if (top < viewportPadding) top = viewportPadding;

    setPosition({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, viewYear]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = (event) => {
      const target = event.target;
      if (
        rootRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const currentMonthKey = formatMonthKey(new Date().getFullYear(), new Date().getMonth());
  const minYear = years[0];
  const maxYear = years[years.length - 1];

  const handleSelect = (monthIndex) => {
    const monthKey = formatMonthKey(viewYear, monthIndex);
    if (!monthMap.has(monthKey)) return;
    onChange(monthKey);
    setOpen(false);
    setHoverMonth('');
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange('');
    setOpen(false);
    setHoverMonth('');
  };

  const handleThisMonth = () => {
    if (monthMap.has(currentMonthKey)) {
      onChange(currentMonthKey);
      setViewYear(new Date().getFullYear());
      setOpen(false);
      setHoverMonth('');
    }
  };

  const previewKey = hoverMonth || (value && value.startsWith(`${viewYear}-`) ? value : '');
  const previewRow = previewKey ? monthMap.get(previewKey) : null;

  const popover = open ? (
    <div
      ref={popoverRef}
      className="sp-month-calendar__popover"
      role="dialog"
      aria-label="Choose month"
      style={{ top: position.top, left: position.left, width: POPOVER_WIDTH }}
    >
      <div className="sp-month-calendar__header">
        <button
          type="button"
          className="sp-month-calendar__nav"
          onClick={() => setViewYear((year) => year - 1)}
          disabled={viewYear <= minYear}
          aria-label="Previous year"
        >
          <FaChevronLeft />
        </button>
        <div className="sp-month-calendar__year-wrap">
          <span className="sp-month-calendar__year">{viewYear}</span>
          <span className="sp-month-calendar__year-hint">Select a month</span>
        </div>
        <button
          type="button"
          className="sp-month-calendar__nav"
          onClick={() => setViewYear((year) => year + 1)}
          disabled={viewYear >= maxYear}
          aria-label="Next year"
        >
          <FaChevronRight />
        </button>
      </div>

      <div className="sp-month-calendar__grid">
        {MONTH_LABELS.map((label, monthIndex) => {
          const monthKey = formatMonthKey(viewYear, monthIndex);
          const row = monthMap.get(monthKey);
          const hasData = Boolean(row);
          const selected = value === monthKey;
          const isCurrent = monthKey === currentMonthKey;

          return (
            <button
              key={monthKey}
              type="button"
              className={[
                'sp-month-calendar__month',
                selected && 'sp-month-calendar__month--selected',
                isCurrent && 'sp-month-calendar__month--current',
                hasData && 'sp-month-calendar__month--has-data',
                !hasData && 'sp-month-calendar__month--disabled',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleSelect(monthIndex)}
              onMouseEnter={() => hasData && setHoverMonth(monthKey)}
              onMouseLeave={() => setHoverMonth('')}
              disabled={!hasData}
              aria-label={
                hasData
                  ? `${formatMonthDisplay(monthKey)}, ${row.receipts} receipts, ${formatMoney(row.sales)}`
                  : `${label} ${viewYear}, no sales`
              }
            >
              <span className="sp-month-calendar__month-label">{label}</span>
              {hasData && <span className="sp-month-calendar__dot" aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <div className={`sp-month-calendar__preview ${previewRow ? 'sp-month-calendar__preview--active' : ''}`}>
        {previewRow ? (
          <>
            <span className="sp-month-calendar__preview-label">{formatMonthDisplay(previewKey)}</span>
            <span className="sp-month-calendar__preview-stat">{previewRow.receipts} receipts</span>
            <span className="sp-month-calendar__preview-sales">{formatMoney(previewRow.sales)}</span>
          </>
        ) : (
          <span className="sp-month-calendar__preview-empty">Hover or select a month to see sales</span>
        )}
      </div>

      <div className="sp-month-calendar__footer">
        <button
          type="button"
          className="sp-month-calendar__action"
          onClick={handleThisMonth}
          disabled={!monthMap.has(currentMonthKey)}
        >
          This month
        </button>
        {value && (
          <button type="button" className="sp-month-calendar__action" onClick={handleClear}>
            Clear
          </button>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="sp-month-calendar" ref={rootRef}>
      <label className="sp-month-picker-label">Select month</label>
      <button
        ref={triggerRef}
        type="button"
        className={`sp-month-calendar__trigger ${open ? 'sp-month-calendar__trigger--open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <FaCalendarAlt className="sp-month-calendar__icon" aria-hidden="true" />
        <span className={value ? 'sp-month-calendar__value' : 'sp-month-calendar__placeholder'}>
          {value ? formatMonthDisplay(value) : placeholder}
        </span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            className="sp-month-calendar__clear"
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClear(e);
              }
            }}
            aria-label="Clear month"
          >
            <FaTimes size={11} />
          </span>
        )}
      </button>

      {typeof document !== 'undefined' && createPortal(popover, document.body)}
    </div>
  );
}
